import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import smoothscroll from "smoothscroll-polyfill";
import { GroupMember, GroupMemberState, MotesPaths } from '../../models';

/**
 * Scoreboard for a group quiz
 */
@Component({
    selector: 'group-scoreboard',
    templateUrl: './group-scoreboard.component.html',
    styleUrls: ['./group-scoreboard.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GroupScoreboardComponent implements OnChanges {
    /** Group members participating in the challenge */
    @Input() members: GroupMember[] = null;
    /** Key for the current user */
    @Input() currentUserKey: string = '';
    /** Key for the challenge */
    @Input() challengeKey: string = null;
    /** The  point goal for the quiz */
    @Input() pointGoal: number = 0;
    /** The number of questions in quiz (max before quiz is regarded as completed) */
    @Input() maxQuizQuestions: number = 0;
    /** Event fired when the modal is closed */
    @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
    /** Reference for the scoreboard element in the template */
    @ViewChild('scoreboard') scoreboard: ElementRef<HTMLElement>;

    /** Flag to determine whether to show results or a loading screen. `true` when the `state` of all group members is `IDLE` */
    public allMembersDone: boolean = false;
    /** Flag to determine whether a leaderboard view should be shown (only top 3 group members) */
    public isLeaderboard: boolean = false;

    // Expose `MotesPaths` to template
    public MotesPaths = MotesPaths;

    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.members) {
            // Sort the group members based on their points in the challenge specified by `challengeKey`, highest to lowest
            this.members.sort((a, b) => b.challengeStateMap.getChallengeState(this.challengeKey).points - a.challengeStateMap.getChallengeState(this.challengeKey).points);
            // Check whether any members have reached the point goal. If so, enable leaderboard view.
            this.isLeaderboard = this.isChallengeCompleted(this.members);
            // Check whether all members have a `state` of COMPLETING (i.e. they are waiting for the scoreboard results) or IDLE (i.e. they have skipped or finished the quiz entirely)
            this.allMembersDone = !this.members.find((member) => !(member.state == GroupMemberState.COMPLETING || member.state == GroupMemberState.IDLE));
            // If leaderboard, remove all members but the top 3
            if(this.isLeaderboard) this.members.splice(3);
        }
    }

    /**
     * Determine if one or mote members of the group has completed the challenge (by max point or question answer count)
     */
    private isChallengeCompleted(members: GroupMember[]): boolean {
        // Get challenge state for all members
        let challengeStates = members.map(member => member.challengeStateMap.getChallengeState(this.challengeKey));
        // List of questions answered by each member
        let memberAnswerCount = challengeStates.map(state => Object.keys(state.pointMap).length);

        // Return true if someone in group has reached the pointGoal
        if(challengeStates.find(state => (state.points >= this.pointGoal))) return true;
        // Return true if someone in the group has answered all questions in the quiz
        if(memberAnswerCount.find(answerCount => (this.maxQuizQuestions && answerCount >= this.maxQuizQuestions))) return true;
        // Return false if quiz is not completed yet
        return false;
    }

    /**
     * Scroll to the position of the current user on the scoreboard
     */
    public scrollToCurrentUser(member: GroupMember): void {
        if(this.members && this.currentUserKey && this.currentUserKey == member.key && !this.isLeaderboard) {
            // Activate polyfill (enabling smooth scroll in Safari)
            smoothscroll.polyfill();
            this.scrollTo(document.querySelector('.score.active') as HTMLElement, this.scoreboard.nativeElement);
        }
    }

    /**
     * Scroll (horizontally) to the position of an element in a container, centering it in the container
     * @param element The element to scroll to
     * @param container The container for the element
     */
    private scrollTo(element: HTMLElement, container: HTMLElement): void {
        if(!element) {
            console.warn("Could not scroll to null element");
            return;
        }

        // Calculate the pixel amount to scroll from the left
        const elementCenter = element.offsetLeft + (element.offsetWidth / 2);
        const containerCenter = container.offsetWidth / 2;
        const leftScrollPosition = elementCenter - containerCenter;

        // Scroll smoothly
        container.scroll({
            left: leftScrollPosition,
            top: 0,
            behavior: 'smooth'
        });
    }

    public trackByUserKey(index: number, groupMember: GroupMember): string {
        return groupMember.key;
    }
}
